import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Copy,
  Download,
  FileText,
  Zap,
  Target,
  Quote,
  CheckCircle,
  AlignLeft,
  Settings,
  Sparkles,
  Clock,
  ListChecks,
  MessageSquare
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../firebase.init";
import { deductUserCredit } from "../../../../utils/geminiUtils";


const ScriptSummaryGenerator = ({ toolPath = "/user/script-summarizer" }) => {
  // --- AUTH & CORE STATE (UNCHANGED) ---
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [loadingTools, setLoadingTools] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // --- FORM STATE ---
  const [scriptText, setScriptText] = useState("");
  const [summaryLength, setSummaryLength] = useState("Medium (400–600 words)");
  const [summaryStyle, setSummaryStyle] = useState("Professional");
  const [outputFormat, setOutputFormat] = useState("Paragraph Summary");
  
  // Toggles
  const [includeQuotes, setIncludeQuotes] = useState(true);
  const [includeTakeaways, setIncludeTakeaways] = useState(true);
  const [includeCta, setIncludeCta] = useState(true);

  // --- OUTPUT STATE ---
  const [resultData, setResultData] = useState(null);

  // --- CREDIT & TOOL CONFIG (UNCHANGED) ---
  
  const [creditCost, setCreditCost] = useState(2);
  const [apiKeys, setApiKeys] = useState([]);

  // --- OPTIONS ---
  const lengthOptions = ["Very Short (100–150 words)", "Short (200–300 words)", "Medium (400–600 words)", "Detailed (800+ words)"];
  const styleOptions = ["Professional", "Casual", "Educational", "Persuasive", "Storytelling"];
  const formatOptions = ["Paragraph Summary", "Bullet Points", "Key Takeaways", "Executive Summary", "Social Media Style"];

  // Helper: Count words
  const getWordCount = (text) => {
    return text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
  };

  // Fetch API keys (Unchanged)
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) setError("No API keys available. Please contact administrator.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Fetch Tools List (Unchanged)
  const fetchToolsList = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/tools-section`);
      if (response.data && response.data.success && response.data.data && response.data.data.tools) {
        const tool = response.data.data.tools.find(t => t.id === 6); // Assuming ID 6 corresponds to this tool
        if (tool) {
            setCreditCost(tool.creditCost);
        }
      }
    } catch (err) {
        console.warn("Could not fetch tools list, using default credit cost");
    } finally {
        setLoadingTools(false);
    }
  };

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // Initial Data Fetch (Unchanged)
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        setLoadingTools(false);
        return;
      }
      try {
        await Promise.all([fetchApiKeys(), fetchToolsList()]);
        
        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
          
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Prompt Engineering for Summary Generation
  const constructPrompt = () => {
    let schemaObj = `{
      "heading": "A clear, engaging title for this summary",
      "summaryBody": "The main summary content, meticulously formatted as '${outputFormat}'. Preserve the original meaning. Do not hallucinate. Use \\n for line breaks."`;
    
    if (includeQuotes) schemaObj += `,\n      "keyQuotes": ["Exact profound quote 1 from the text", "Exact quote 2"]`;
    if (includeTakeaways) schemaObj += `,\n      "actionableTakeaways": ["Action step or core lesson 1", "Action step 2"]`;
    if (includeCta) schemaObj += `,\n      "ctaSuggestion": "A suggested soft call-to-action based on the material"` ;
    
    schemaObj += `\n    }`;

    return `You are an expert editor, content strategist, and summarization assistant. 
    Analyze the provided long-form script/transcript and generate a structured summary based on these exact parameters:

    - Target Length: ${summaryLength}
    - Tone/Style: ${summaryStyle}
    - Output Format: ${outputFormat}
    
    RULES:
    1. Preserve the original meaning and core message.
    2. Remove all fluff, repetition, and filler words.
    3. Do NOT add external facts or hallucinate information not present in the source text.
    4. Keep the language clear and highly readable.
    
    SOURCE TEXT:
    """
    ${scriptText}
    """

    CRITICAL INSTRUCTION:
    Respond ONLY with a raw, valid JSON object matching this schema exactly. Do not wrap in markdown \`\`\`json blocks.
    
    SCHEMA:
    ${schemaObj}`;
  };

  // Generate content (Credit deduction logic exactly as requested)
  const generateSummary = async () => {
    if (!scriptText.trim()) {
      setError("Please paste a script or transcript to summarize.");
      return;
    }
    if (apiKeys.length === 0) {
      setError("No API keys available. Please try again later.");
      return;
    }
    

    setGenerating(true);
    setError("");
    setResultData(null);

    try {
      // Deduct credits (Unchanged)
      
      
      

      // Call AI
      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      const prompt = constructPrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.4, // Lower temperature for more accurate summarization
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 4000,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data.candidates && data.candidates.length > 0 && data.candidates[0].content) {
        let generatedText = data.candidates[0].content.parts[0].text;
        
        // Clean markdown backticks to parse JSON safely
        generatedText = generatedText.replace(/```json/g, '').replace(/```/g, '').trim();
        
        try {
          const parsedContent = JSON.parse(generatedText);
          setResultData(parsedContent);
          toast.success("Summary generated successfully!");
        } catch (parseErr) {
          throw new Error("AI returned invalid data format. Please try again.");
        }
      } else {
        throw new Error("No content was generated from the API response");
      }
    } catch (err) {
      console.error("Error generating content:", err);
      setError(`Error: ${err.message}`);
      toast.error(`Error: ${err.message}`);

      
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      toast.error(`Failed to copy ${label}`);
    });
  };

  const downloadContent = () => {
    if (!resultData) return;
    
    let text = `DOCUMENT SUMMARY\n\n`;
    text += `=== ${resultData.heading.toUpperCase()} ===\n\n`;
    text += `${resultData.summaryBody}\n\n`;
    
    if (resultData.keyQuotes && resultData.keyQuotes.length > 0) {
      text += `=== KEY QUOTES ===\n`;
      resultData.keyQuotes.forEach(quote => text += `"${quote}"\n\n`);
    }
    
    if (resultData.actionableTakeaways && resultData.actionableTakeaways.length > 0) {
      text += `=== ACTIONABLE TAKEAWAYS ===\n`;
      resultData.actionableTakeaways.forEach(takeaway => text += `- ${takeaway}\n`);
      text += `\n`;
    }

    if (resultData.ctaSuggestion) {
      text += `=== CTA SUGGESTION ===\n${resultData.ctaSuggestion}\n`;
    }
    
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Document-Summary.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Summary downloaded!");
  };

  if (loadingAuth || loading || loadingKeys || loadingTools) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
            <div style={{ border: '4px solid #f1f5f9', borderTop: '4px solid #3b82f6', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', margin: '0 auto 10px' }}></div>
            <p style={{ color: '#64748b', fontWeight: '500' }}>Loading Workspace...</p>
        </div>

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <div style={{ background: 'white', padding: '3rem', borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)', textAlign: 'center', maxWidth: '400px' }}>
          <FileText size={48} color="#3b82f6" style={{ marginBottom: '1rem' }} />
          <h2 style={{ color: '#0f172a', marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '700' }}>Authentication Required</h2>
          <button onClick={() => (window.location.href = "/login")} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', width: '100%' }}>
            Proceed to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .summary-app { font-family: 'Inter', system-ui, sans-serif; background: #f8fafc; min-height: 100vh; padding: 2rem 1rem; color: #334155; }
        .saas-card { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); border: 1px solid #e2e8f0; padding: 1.75rem; height: 100%; }
        .form-label { font-weight: 600; color: #475569; font-size: 0.9rem; margin-bottom: 0.5rem; display: block; }
        .form-control, .form-select { width: 100%; padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 0.95rem; color: #0f172a; transition: all 0.2s; margin-bottom: 0.5rem; box-sizing: border-box; }
        .form-control:focus, .form-select:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); background: #fff; }
        textarea.form-control { resize: vertical; min-height: 200px; line-height: 1.5; }
        
        .char-counter { font-size: 0.8rem; color: #64748b; text-align: right; margin-bottom: 1.25rem; font-weight: 500; }
        
        .gradient-btn { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; border: none; padding: 1rem 1.5rem; border-radius: 10px; font-weight: 600; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.3s ease; width: 100%; box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25); }
        .gradient-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35); }
        .gradient-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
        
        .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; border-radius: 34px; }
        .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
        input:checked + .slider { background-color: #10b981; }
        input:checked + .slider:before { transform: translateX(20px); }

        .result-section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 1.5rem; margin-bottom: 1.5rem; position: relative; }
        .result-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; padding-bottom: 0.75rem; border-bottom: 1px solid #e2e8f0; }
        .result-title { font-size: 1.1rem; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 8px; margin: 0; }
        .copy-btn { background: white; border: 1px solid #cbd5e1; color: #64748b; padding: 6px 12px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: all 0.2s; }
        .copy-btn:hover { background: #f1f5f9; color: #0f172a; }
        
        .summary-body { font-size: 1.05rem; line-height: 1.8; color: #1e293b; white-space: pre-wrap; margin: 0; }
        .quote-box { border-left: 3px solid #3b82f6; padding-left: 1rem; margin-bottom: 1rem; font-style: italic; color: #475569; }
        
        @keyframes custom-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.98); } }
        .anim-pulse { animation: custom-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>

      <div className="summary-app">
       
        
        <div className="container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Top Navigation */}
          <div className="row mb-4">
            <div className="col-12 d-flex justify-content-between align-items-center" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => window.history.back()} style={{ background: "#fff", border: "1px solid #e2e8f0", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", color: "#64748b" }}>
                ← Dashboard
              </button>
              
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-5" style={{ textAlign: "center", marginBottom: "3rem" }}>
            <h1 style={{ fontSize: "2.5rem", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", letterSpacing: "-0.02em" }}>
              <FileText color="#3b82f6" size={40} /> Long Script Summarizer
            </h1>
            <p style={{ color: "#64748b", marginTop: "12px", fontSize: "1.1rem", maxWidth: "650px", margin: "12px auto 0" }}>
              Instantly convert lengthy scripts, transcripts, or articles into clear, structured summaries tailored for any platform.
            </p>
          </div>

          {error && (
            <div style={{ background: "#fef2f2", color: "#ef4444", padding: "1rem", borderRadius: "10px", marginBottom: "2rem", textAlign: "center", border: "1px solid #fecaca", fontWeight: "600" }}>
              {error}
            </div>
          )}

          <div className="row" style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
            
            {/* Left Column - Form */}
            <div className="col-lg-5 col-md-6" style={{ flex: '1 1 400px', maxWidth: '500px' }}>
              <div className="saas-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #f1f5f9' }}>
                  <AlignLeft size={20} color="#3b82f6" />
                  <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a', fontWeight: '700' }}>Input & Settings</h3>
                </div>
                
                <label className="form-label">Long Script / Transcript <span style={{color:'#ef4444'}}>*</span></label>
                <textarea
                  className="form-control"
                  value={scriptText}
                  onChange={(e) => setScriptText(e.target.value)}
                  placeholder="Paste your long-form content, YouTube transcript, or blog post here..."
                />
                <div className="char-counter">
                  {getWordCount(scriptText)} words | {scriptText.length} characters
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Summary Length</label>
                    <select className="form-select" value={summaryLength} onChange={(e) => setSummaryLength(e.target.value)} style={{ marginBottom: 0 }}>
                      {lengthOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Summary Tone</label>
                    <select className="form-select" value={summaryStyle} onChange={(e) => setSummaryStyle(e.target.value)} style={{ marginBottom: 0 }}>
                      {styleOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                </div>

                <label className="form-label">Output Format</label>
                <select className="form-select" value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)}>
                  {formatOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>

                <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', marginTop: '0.5rem', marginBottom: '1.5rem', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>Extract Key Quotes</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Pull impactful sentences</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeQuotes} onChange={(e) => setIncludeQuotes(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>Actionable Takeaways</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Generate actionable steps</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeTakeaways} onChange={(e) => setIncludeTakeaways(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#1e293b', display: 'block', fontSize: '0.9rem' }}>CTA Suggestion</span>
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Recommend a call-to-action</span>
                    </div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={includeCta} onChange={(e) => setIncludeCta(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                </div>

                <button onClick={generateSummary} disabled={generating || !scriptText.trim() } className="gradient-btn">
                  {generating ? <Clock size={18} className="fa-spin" /> : <Sparkles size={18} />}
                  {generating ? "Condensing Content..." : `Generate Summary `}
                </button>
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="col-lg-7 col-md-6" style={{ flex: '1 1 450px' }}>
              
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h2 style={{ margin: 0, fontSize: "1.5rem", color: "#0f172a", fontWeight: "700" }}>Generated Summary</h2>
                {resultData && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                     <button onClick={() => copyToClipboard(resultData.summaryBody, "Summary")} style={{ background: "#fff", color: "#334155", border: "1px solid #cbd5e1", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "0.9rem", fontWeight: "600" }}>
                      <Copy size={16} /> Copy
                    </button>
                    <button onClick={downloadContent} style={{ background: "#3b82f6", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "0.9rem", fontWeight: "600", boxShadow: "0 2px 4px rgba(59, 130, 246, 0.2)" }}>
                      <Download size={16} /> Export
                    </button>
                  </div>
                )}
              </div>

              {generating ? (
                 <div className="saas-card anim-pulse" style={{ textAlign: "center", padding: "6rem 2rem", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '70px', height: '70px', background: '#eff6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                      <Settings size={34} color="#3b82f6" className="fa-spin" />
                    </div>
                    <h3 style={{ color: '#0f172a', fontWeight: '800', marginBottom: '0.5rem' }}>Processing Long-Form Content...</h3>
                    <p style={{ color: "#64748b", maxWidth: "350px", margin: "0 auto" }}>Extracting core value, removing fluff, and structuring your requested output.</p>
                 </div>
              ) : resultData ? (
                <div className="saas-card" style={{ padding: '2rem' }}>
                  
                  {/* Main Summary */}
                  <div className="result-section" style={{ background: '#fff', borderLeft: '4px solid #3b82f6', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                    <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#0f172a', marginBottom: '1rem' }}>
                      {resultData.heading}
                    </h3>
                    <div className="summary-body">
                      {resultData.summaryBody}
                    </div>
                  </div>

                  {/* Key Quotes */}
                  {resultData.keyQuotes && resultData.keyQuotes.length > 0 && (
                    <div className="result-section">
                      <div className="result-header">
                        <h3 className="result-title"><Quote size={18} color="#8b5cf6" /> Extracted Quotes</h3>
                        <button className="copy-btn" onClick={() => copyToClipboard(resultData.keyQuotes.join("\n"), "Quotes")}>
                          <Copy size={14} /> Copy
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {resultData.keyQuotes.map((quote, idx) => (
                          <div key={idx} className="quote-box">"{quote}"</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actionable Takeaways */}
                  {resultData.actionableTakeaways && resultData.actionableTakeaways.length > 0 && (
                    <div className="result-section" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                      <div className="result-header" style={{ borderColor: '#bbf7d0' }}>
                        <h3 className="result-title" style={{ color: '#166534' }}><ListChecks size={18} color="#16a34a" /> Actionable Takeaways</h3>
                        <button className="copy-btn" onClick={() => copyToClipboard(resultData.actionableTakeaways.join("\n"), "Takeaways")} style={{ borderColor: '#bbf7d0' }}>
                          <Copy size={14} /> Copy
                        </button>
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#15803d', fontWeight: '500', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {resultData.actionableTakeaways.map((takeaway, idx) => (
                          <li key={idx}>{takeaway}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* CTA Suggestion */}
                  {resultData.ctaSuggestion && (
                    <div className="result-section" style={{ background: '#fffbeb', border: '1px solid #fde68a', marginBottom: 0 }}>
                      <div className="result-header" style={{ borderColor: '#fde68a' }}>
                        <h3 className="result-title" style={{ color: '#b45309' }}><Target size={18} color="#f59e0b" /> CTA Suggestion</h3>
                      </div>
                      <p style={{ margin: 0, color: '#92400e', fontWeight: '500', fontSize: '1.05rem' }}>
                        {resultData.ctaSuggestion}
                      </p>
                    </div>
                  )}

                </div>
              ) : (
                <div className="saas-card" style={{ textAlign: 'center', padding: '6rem 2rem', border: '2px dashed #cbd5e1', background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <MessageSquare size={56} color="#cbd5e1" style={{ marginBottom: '1.5rem' }} />
                  <h3 style={{ color: '#64748b', fontWeight: '700', margin: '0 0 0.5rem 0' }}>Ready to Summarize</h3>
                  <p style={{ color: '#94a3b8', margin: 0, maxWidth: '350px' }}>Paste your long-form text on the left to extract value, remove fluff, and build a clean summary.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScriptSummaryGenerator;